import { Button, Group } from '@mantine/core';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type { ProcessingCardActions } from './ProcessingCard.types';

interface Props {
	actions: ProcessingCardActions;
}

/** While processing: Cancel and "Notify me when complete", plus the modal's "Run in background" and "Open analysis". */
export const ProcessingFooterActions = ({ actions }: Props) => {
	const tCapManage = useTranslationCapManage();
	const isModalLayout = !!actions.onRunInBackground || !!actions.onOpenAnalysis;

	// No side padding, so it sits 8px after Cancel and flush with the footer edge.
	const notifyButton = (
		<Button
			variant="link-color"
			size="height-36"
			px={0}
			onClick={actions.onNotify}
			data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_NOTIFY_BUTTON}
		>
			{tCapManage('dealstack_ai.ingest.processing.notify')}
		</Button>
	);

	return (
		<Group
			gap="xs"
			p="sm"
			wrap="nowrap"
			style={{ borderTop: '1px solid var(--mantine-color-gray-4)' }}
		>
			<Group gap="xs" wrap="nowrap">
				<Button
					size="height-36"
					variant="default"
					fw={600}
					onClick={actions.onCancel}
					loading={actions.isCancelling}
					data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_PROCESSING_CANCEL_BUTTON}
				>
					{tCapManage('dealstack_ai.ingest.processing.cancel')}
				</Button>
				{isModalLayout && notifyButton}
			</Group>
			<Group gap="xs" wrap="nowrap" ml="auto">
				{!isModalLayout && notifyButton}
				{actions.onRunInBackground && (
					<Button
						size="height-36"
						variant="default"
						fw={600}
						onClick={actions.onRunInBackground}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_RUN_IN_BACKGROUND_BUTTON}
					>
						{tCapManage('dealstack_ai.ingest.processing.run_in_background')}
					</Button>
				)}
				{actions.onOpenAnalysis && (
					<Button
						size="height-36"
						fw={600}
						onClick={actions.onOpenAnalysis}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_OPEN_ANALYSIS_BUTTON}
					>
						{tCapManage('dealstack_ai.ingest.processing.open_analysis')}
					</Button>
				)}
			</Group>
		</Group>
	);
};
